import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MonoTypeOperatorFunction, Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ListViewTagFilterParameter, PaginationState } from '@coin/shared/core/util-models';

@Injectable({
  providedIn: 'root'
})
export class HttpHelpersService {
  private toast = inject(ToastrService);
  private translateService = inject(TranslateService);

  public handleSuccess<T>(message: string): MonoTypeOperatorFunction<T> {
    return tap(() => {
      this.toast.success(this.translateService.instant(message));
    });
  }

  public handleInfo<T>(message: string): MonoTypeOperatorFunction<T> {
    return tap(() => {
      this.toast.info(this.translateService.instant(message));
    });
  }

  public handleError<T, E = string>(
    message: string,
    customErrorProperty = 'reason',
    transform: (error: E) => string = error => (error ? String(error) : undefined)
  ): MonoTypeOperatorFunction<T> {
    return catchError((res: HttpErrorResponse) => {
      const errorReason = transform(res?.error?.[customErrorProperty]) || 'Unknown';
      return this.throwError<T>(message, errorReason);
    });
  }

  public throwError<T>(message: string, errorReason?: string): Observable<T> {
    let errorMessage = this.translateService.instant(message);

    if (errorReason) {
      errorMessage += `. ${errorReason}`;
    }

    this.toast.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  public withStatusMessages<T>({
    error,
    success,
    info
  }: {
    error?: string;
    success?: string;
    info?: string;
  }): MonoTypeOperatorFunction<T> {
    return source$ =>
      source$.pipe(
        error ? this.handleError(error) : tap(), // Add error handling
        success ? this.handleSuccess(success) : tap(), // Add success handling
        info ? this.handleInfo(info) : tap() // Add info handling
      );
  }

  public getHttpParams(
    paginationState: PaginationState,
    filters: ListViewTagFilterParameter[],
    filterPrefix = 'Query.',
    paginationPrefix = 'Paging.'
  ): HttpParams {
    let params = new HttpParams();
    if (paginationState?.currentPage) {
      params = params.set(`${paginationPrefix}Page`, paginationState.currentPage);
    }
    if (paginationState?.pageSize) {
      params = params.set(`${paginationPrefix}Size`, paginationState.pageSize);
    }
    for (const filter of filters) {
      params = params.set(`${filterPrefix}${filter.category}`, filter.value);
    }
    return params;
  }
}
